import { useState, type FormEvent } from 'react';
import { t } from '../i18n';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { Button, Callout, Field, Input } from '../components/ui';

export function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  return (
    <div className="login">
      {mode === 'login' ? <LoginForm onRegister={() => setMode('register')} /> : <RegisterForm onBack={() => setMode('login')} />}
    </div>
  );
}

function Brand() {
  return (
    <div className="brand" style={{ padding: 0, justifyContent: 'space-between' }}>
      <img className="brand-logo" src="/brand/logo.png" alt={t.app.name} />
      <span className="brand-sub">{t.app.sub}</span>
    </div>
  );
}

function LoginForm({ onRegister }: { onRegister: () => void }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ text: string; tone: 'bad' | 'info' } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      const pending = err instanceof ApiError && err.code === 'pending_approval';
      setError({ text: err instanceof ApiError ? err.message : t.common.unknownError, tone: pending ? 'info' : 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="login-card panel" onSubmit={submit} noValidate>
      <div className="panel-body stack">
        <Brand />
        <h1>{t.auth.title}</h1>
        {error ? <Callout tone={error.tone}>{error.text}</Callout> : null}
        <Field label={t.auth.email} htmlFor="email">
          <Input id="email" type="email" autoComplete="username" ltr value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </Field>
        <Field label={t.auth.password} htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" ltr value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" variant="primary" loading={busy} disabled={!email || !password}>
          {busy ? t.auth.submitting : t.auth.submit}
        </Button>
        <p className="small muted" style={{ textAlign: 'center' }}>
          עובד/ת חדש/ה? <button type="button" className="link-button" onClick={onRegister}>הרשמה למערכת</button>
        </p>
      </div>
    </form>
  );
}

function RegisterForm({ onBack }: { onBack: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api<{ message: string }>('/api/auth/register', { body: { name: name.trim(), email: email.trim(), password } });
      setDone(r.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.unknownError);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="login-card panel">
        <div className="panel-body stack">
          <Brand />
          <h1>הבקשה נשלחה</h1>
          <Callout tone="ok">{done}</Callout>
          <Button onClick={onBack}>חזרה לכניסה</Button>
        </div>
      </div>
    );
  }

  return (
    <form className="login-card panel" onSubmit={submit} noValidate>
      <div className="panel-body stack">
        <Brand />
        <h1>הרשמה לעובדים</h1>
        <p className="small muted">אחרי ההרשמה מנהל המערכת יאשר את החשבון ויקבע את ההרשאות שלך.</p>
        {error ? <Callout tone="bad">{error}</Callout> : null}
        <Field label="שם מלא" htmlFor="reg-name" required>
          <Input id="reg-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t.auth.email} htmlFor="reg-email" required>
          <Input id="reg-email" type="email" autoComplete="email" ltr value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="סיסמה" hint="לפחות 10 תווים" htmlFor="reg-password" required>
          <Input id="reg-password" type="password" autoComplete="new-password" ltr value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="אימות סיסמה" htmlFor="reg-confirm" required error={mismatch ? 'הסיסמאות אינן זהות' : null}>
          <Input id="reg-confirm" type="password" autoComplete="new-password" ltr value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" loading={busy} disabled={name.trim().length < 2 || !email.includes('@') || password.length < 10 || confirm !== password}>
          שליחת בקשת הרשמה
        </Button>
        <p className="small muted" style={{ textAlign: 'center' }}>
          כבר רשום/ה? <button type="button" className="link-button" onClick={onBack}>כניסה</button>
        </p>
      </div>
    </form>
  );
}
